import { observer } from 'mobx-react-lite';
import Image from 'next/image';
import { useRouter } from 'next/router';

import Button from '@/components/common/Button/Button';
import type { MenuHierarchyRecordModelType } from '@/models/api';

interface INoProductFound {
  filters: any;
  menuHierarchyCms?: MenuHierarchyRecordModelType;
  searchText?: string;
}

export const NoProductFound: React.FC<INoProductFound> = observer(
  ({ filters, menuHierarchyCms, searchText }) => {
    const { openFilter } = filters;
    const router = useRouter();
    const slug = menuHierarchyCms?.slug || '';

    const content = {
      default: {
        iconSrc: '/assets/images/svg/search.svg',
        title: 'We found no results.',
        subtitle:
          'Try changing or clearing your filter options for more results.',
        buttonText: 'Change Filters',
        buttonClass: 'lg:hidden',
        handleClick: () => openFilter(),
      },
      'liked-products': {
        iconSrc: '/assets/images/svg/search.svg',
        title: 'You currently have no liked products.',
        subtitle:
          'Start liking to create your own easy access list that makes finding and ordering your favourites faster!',
        buttonText: 'Browse Full Menu',
        buttonClass: '',
        handleClick: () => router.push('/menu/meals'),
      },
      specials: {
        iconSrc: '/assets/images/svg/cart-attention.svg',
        title: 'This week’s Chef’s Specials are sold out!',
        subtitle:
          'Look out for new Specials every Sunday. In the meantime check out our Bundles to enjoy great value on our most popular products!',
        buttonText: 'Shop Bundles',
        buttonClass: '',
        handleClick: () => router.push('/menu/bundles'),
      },
      search: {
        iconSrc: '/assets/images/svg/search.svg',
        title: 'We found no results',
        subtitle:
          'Please double-check your spelling or try using broader search terms.',
        buttonText: 'Change Filters',
        buttonClass: 'lg:hidden',
        handleClick: () => openFilter(),
      },
    };

    const shownContent = searchText
      ? content.search
      : content[slug as keyof typeof content] || content.default;

    return (
      <div className="mx-auto flex h-full w-[22rem] flex-col items-center justify-center py-16 md:w-96 2xl:w-[500px] 2xl:py-32">
        <div className="mb-3">
          <Image
            src={shownContent.iconSrc}
            alt="arrow"
            width={40}
            height={40}
            priority
          />
        </div>
        <span className="mb-6 block w-full truncate text-center font-interSemibold text-2xl 2xl:text-4xl">
          {shownContent.title} {searchText && `for "${searchText}"`}
        </span>

        <span className="mb-8 text-center font-interMedium text-base leading-[22px] 2xl:w-96 2xl:text-2xl 2xl:leading-8">
          {shownContent.subtitle}
        </span>
        <div className={`${shownContent.buttonClass}`}>
          <Button
            className="px-10"
            theme="primary"
            onClick={shownContent.handleClick}
          >
            {shownContent.buttonText}
          </Button>
        </div>
      </div>
    );
  },
);
